import { rem } from 'polished';
import styled, { css } from 'styled-components';
import Config from '@config';
import variables from '@styles/variables';

interface StyledTextProps {
  $type?: string;
  $color?: string;
  $size?: string;
  $align?: string;
  $ellipsis?: boolean;
}

export const CommonStyledText = css<StyledTextProps>`
  margin: 0;

  ${({ $color }) => {
    const colorMap: Record<string, string | undefined> = {
      red: variables.colors.primary,
      black: variables.colors.lighterBlack,
      darkGray: variables.colors.darkGray,
      darkerGray: variables.colors.darkerGray,
      white: variables.colors.white,
      blue: variables.colors.highlight,
      error: variables.colors.primaryDark,
      lighterBlack: variables.colors.lighterBlack,
    };

    return $color && colorMap[$color]
      ? css`
          color: ${colorMap[$color]};
        `
      : '';
  }}

  ${({ $align }) => {
    const alignMap: Record<string, string | undefined> = {
      left: 'left',
      center: 'center',
      right: 'right',
    };

    return $align && alignMap[$align]
      ? css`
          text-align: ${alignMap[$align]};
        `
      : '';
  }}

  ${(props) =>
    props.$type === 'intro' &&
    css`
      font-family: ${variables.fonts.fontGothamBook};
      font-size: ${rem(18)};
      line-height: ${rem(28)};
    `}

  ${(props) =>
    props.$type === 'body' &&
    css`
      font-family: ${variables.fonts.fontGothamBook};
      font-size: ${rem(15)};
      line-height: ${rem(24)};

      strong {
        font-family: ${variables.fonts.fontGothamMedium};
      }

      ${props.$size === 'xsmall' &&
      css`
        font-size: ${rem(11)};
        line-height: ${rem(16)};
      `}

      ${props.$size === 'small' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(20)};
      `}

      ${props.$size === 'responsive' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(20)};

        ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
          font-size: ${rem(15)};
          line-height: ${rem(24)};
        }
      `}
    `}

  ${(props) =>
    props.$type === 'detail' &&
    css`
      font-family: ${variables.fonts.fontGothamBook};
      font-size: ${rem(11)};
      line-height: ${rem(16)};

      ${props.$size === 'responsive' &&
      css`
        font-size: ${rem(11)};
        line-height: ${rem(16)};

        ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
          font-size: ${rem(13)};
          line-height: ${rem(20)};
        }
      `}
    `}

  ${(props) =>
    props.$type === 'subhead' &&
    css`
      font-family: ${variables.fonts.fontGothamMedium};
      font-size: ${rem(15)};
      line-height: ${rem(24)};

      ${props.$size === 'xsmall' &&
      css`
        font-size: ${rem(11)};
        line-height: ${rem(16)};
      `}

      ${props.$size === 'small' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(20)};
      `}

      ${props.$size === 'medium' &&
      css`
        font-size: ${rem(15)};
        line-height: ${rem(24)};
      `}

      ${props.$size === 'large' &&
      css`
        font-size: ${rem(18)};
        line-height: ${rem(28)};
        font-weight: 500;
      `}

      ${props.$size === 'responsive' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(20)};

        ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
          font-size: ${rem(15)};
          line-height: ${rem(24)};
        }
      `}
    `}

  ${(props) =>
    props.$type === 'subtitle' &&
    css`
      font-family: ${variables.fonts.fontGothamMedium};

      font-size: ${rem(15)};
      line-height: ${rem(24)};

      ${props.$size === 'large' &&
      css`
        font-size: ${rem(18)};
        line-height: ${rem(28)};
      `}

      ${props.$size === 'small' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(16)};
      `}

      ${props.$size === 'xsmall' &&
      css`
        font-size: ${rem(11)};
        line-height: ${rem(16)};
      `}
    `}

    
    ${(props) =>
    props.$type === 'label' &&
    css`
      font-family: ${variables.fonts.fontGothamMedium};
      text-transform: uppercase;

      // The default size for labels is currently set to small.
      // For most other text components default is medium.
      // For the sake of consistency it would be nice to
      // standardize on a default size across all types, ex. medium.

      // Alternatively, we could consider requiring an explicit size prop to be set for
      // all text components to improve readability and avoid assumptions.

      // 2025-05-28: Added style for size small (same as default).
      // Labels with this prop existed before, but it had no effect and defaulted to small.

      // Potential TODO:
      // Replace empty <Label> with <Label size="small">

      font-size: ${rem(13)};
      line-height: ${rem(20)};

      ${props.$size === 'xsmall' &&
      css`
        font-size: ${rem(10)};
        line-height: ${rem(14)};
      `}

      ${props.$size === 'small' &&
      css`
        font-size: ${rem(13)};
        line-height: ${rem(20)};
      `}

      ${props.$size === 'medium' &&
      css`
        font-size: ${rem(15)};
        line-height: ${rem(24)};
      `}
    `}


  ${(props) =>
    props.$type === 'price' &&
    css`
      font-family: ${variables.fonts.fontGothamSerifBold};

      font-size: ${rem(20)};
      line-height: ${rem(24)};

      ${props.$size === 'small' &&
      css`
        font-size: ${rem(13)};
      `}

      ${props.$size === 'xsmall' &&
      css`
        font-family: ${variables.fonts.fontGothamMedium};
        font-size: ${rem(11)};
      `}

      ${props.$size === 'medium' &&
      css`
        font-size: ${rem(16)};
        line-height: ${rem(20)};
      `}

      ${props.$size === 'large' &&
      css`
        font-size: ${rem(28)};
        line-height: ${rem(32)};
      `}
    `}

  ${(props) =>
    props.$ellipsis &&
    css`
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}
`;

export const StyledTextParagraph = styled.p<StyledTextProps>`
  ${CommonStyledText}
`;

export const StyledTextSpan = styled.span<StyledTextProps>`
  ${CommonStyledText}
`;
